import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  SlidersHorizontal,
  MoreHorizontal,
  Eye,
  Trash,
  FileText,
  CheckCircle,
  CircleAlert,
  Filter,
  X,
  Loader2,
  Send,
  Plus,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { navigate } from "wouter/use-browser-location";
import { useQuotations, useDeleteQuotation, useCloneQuotation, useSendQuotationEmail } from "@/hooks/useQuotations";
import { QuotationListItem, QuotationStatus } from "@/types/salesDocuments";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const cn = (...classes: (string | boolean)[]) =>
  classes.filter(Boolean).join(" ");

export const Quotations = () => {
  const { formatTotalAmount } = useDecimalPrecision();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [selectedQuotations, setSelectedQuotations] = React.useState<string[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [visibleColumns, setVisibleColumns] = React.useState({
    codeNumber: true,
    buyer: true,
    quotationDate: true,
    validUntil: true,
    amount: true,
    status: true,
    emailStatus: true,
    actions: true,
  });
  const [dropdownOpenVisibleColumn, setDropdownOpenVisibleColumn] = React.useState(false);
  const [openDropdownRowId, setOpenDropdownRowId] = React.useState<string | null>(null);

  const itemsPerPage = 10;

  const { data: quotationsData, isLoading } = useQuotations();
  const deleteQuotationMutation = useDeleteQuotation();
  const cloneQuotationMutation = useCloneQuotation();
  const sendEmailMutation = useSendQuotationEmail();

  const quotations = quotationsData?.data || [];

  const statuses = [
    "all",
    QuotationStatus.DRAFT,
    QuotationStatus.SENT,
    QuotationStatus.ACCEPTED,
    QuotationStatus.REJECTED,
    QuotationStatus.EXPIRED,
    QuotationStatus.CONVERTED,
  ];

  // Reset pagination when filters change
  const resetPagination = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  React.useEffect(() => {
    resetPagination();
  }, [filter, statusFilter, dateRange]);

  // Enhanced filter logic
  const filteredData = quotations.filter((quotation: QuotationListItem) => {
    const buyerName = quotation.buyerName ?? "Not Found";

    const matchesSearch =
      buyerName.toLowerCase().includes(filter.toLowerCase()) ||
      quotation.codeNumber.toLowerCase().includes(filter.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || quotation.status === statusFilter;

    const quotationDate = new Date(quotation.quotationDate);
    const matchesDateRange =
      (!dateRange.from || quotationDate >= dateRange.from) &&
      (!dateRange.to || quotationDate <= dateRange.to);

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  // Calculate statistics
  const stats = {
    totalQuotations: filteredData.length,
    totalAmount: filteredData.reduce(
      (sum: number, quotation: QuotationListItem) => sum + Number(quotation.totalPayableAmount),
      0
    ),
    draftQuotations: filteredData.filter(
      (q: QuotationListItem) => q.status === QuotationStatus.DRAFT
    ).length,
    sentQuotations: filteredData.filter(
      (q: QuotationListItem) => q.status === QuotationStatus.SENT
    ).length,
    convertedQuotations: filteredData.filter(
      (q: QuotationListItem) => q.status === QuotationStatus.CONVERTED
    ).length,
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuotations(filteredData.map((q: QuotationListItem) => q.id));
    } else {
      setSelectedQuotations([]);
    }
  };

  const handleSelectQuotation = (checked: string | boolean, id: string) => {
    if (checked) {
      setSelectedQuotations([...selectedQuotations, id]);
    } else {
      setSelectedQuotations(selectedQuotations.filter((selectedId) => selectedId !== id));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteQuotationMutation.mutateAsync(id);
      toast({
        title: "Deleted",
        description: "Quotation has been deleted",
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to delete quotation",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const handleClone = async (id: string) => {
    try {
      await cloneQuotationMutation.mutateAsync(id);
      toast({
        title: "Success",
        description: "Quotation has been cloned",
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to clone quotation",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const handleBatchClone = async () => {
    let successCount = 0;
    for (const id of selectedQuotations) {
      try {
        await cloneQuotationMutation.mutateAsync(id);
        successCount++;
      } catch {
        // continue
      }
    }
    toast({
      title: "Clone Complete",
      description: `${successCount} quotation(s) cloned successfully`,
      icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
      duration: 5000,
    });
    queryClient.invalidateQueries({ queryKey: ["quotations"] });
    setSelectedQuotations([]);
  };

  const handleBatchSendEmail = async () => {
    try {
      await sendEmailMutation.mutateAsync(selectedQuotations);
      toast({
        title: "Sending",
        description: `Sending email for ${selectedQuotations.length} quotation(s)`,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      setSelectedQuotations([]);
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to send emails",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const getEmailStatusColor = (status: string | null) => {
    const base = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case "Sending": return `${base} bg-yellow-100 text-yellow-800`;
      case "Sent": return `${base} bg-green-100 text-green-800`;
      case "Error": return `${base} bg-red-100 text-red-800`;
      default: return `${base} bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getStatusColor = (status: QuotationStatus) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case QuotationStatus.DRAFT:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case QuotationStatus.SENT:
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case QuotationStatus.ACCEPTED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case QuotationStatus.REJECTED:
        return `${baseStyle} bg-red-100 text-red-800`;
      case QuotationStatus.EXPIRED:
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case QuotationStatus.CONVERTED:
        return `${baseStyle} bg-purple-100 text-purple-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <Button
          key="1"
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(1)}
        >
          1
        </Button>
      );

      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis-end">...</span>);
      }

      buttons.push(
        <Button
          key={totalPages}
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  const activeFilters = [
    statusFilter !== "all" && { type: "Status", value: statusFilter },
    dateRange.from && { type: "From", value: format(dateRange.from, "PP") },
    dateRange.to && { type: "To", value: format(dateRange.to, "PP") },
  ].filter(Boolean);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Quotations</h1>
        <Button onClick={() => navigate("/quotation/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Create Quotation
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              RM {formatTotalAmount(stats.totalAmount)}
            </div>
            <p className="text-xs text-muted-foreground">Total quotation value</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quotations</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalQuotations}</div>
            <p className="text-xs text-muted-foreground">Across all statuses</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Draft</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draftQuotations}</div>
            <p className="text-xs text-muted-foreground">Pending drafts</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sent</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.sentQuotations}</div>
            <p className="text-xs text-muted-foreground">Sent to customers</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Converted</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.convertedQuotations}</div>
            <p className="text-xs text-muted-foreground">To sales orders</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center gap-4">
            <Input
              placeholder="Search quotation number, buyer..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-sm"
            />

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses
                  .filter((status) => status !== "all")
                  .map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[240px] justify-start text-left font-normal",
                    !dateRange.from && !dateRange.to && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "PP")} - {format(dateRange.to, "PP")}
                      </>
                    ) : (
                      format(dateRange.from, "PP")
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) =>
                    setDateRange({ from: range?.from, to: range?.to })
                  }
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <DropdownMenu
            open={dropdownOpenVisibleColumn}
            onOpenChange={(val) => setDropdownOpenVisibleColumn(val)}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-4">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibleColumns).map(([key, value]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  className="capitalize"
                  checked={value}
                  onCheckedChange={(checked) =>
                    setVisibleColumns((prev) => ({ ...prev, [key]: checked }))
                  }
                >
                  {key}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Batch actions */}
        <div className="h-9">
          {selectedQuotations.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedQuotations.length} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchSendEmail}
                disabled={sendEmailMutation.isPending}
              >
                {sendEmailMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Email
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchClone}
                disabled={cloneQuotationMutation.isPending}
              >
                {cloneQuotationMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Clone
              </Button>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <Badge
                key={filter ? `${filter.type}-${filter.value}` : undefined}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {filter && `${filter.type}: ${filter.value}`}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => {
                    if (filter) {
                      switch (filter.type) {
                        case "Status":
                          setStatusFilter("all");
                          break;
                        case "From":
                        case "To":
                          setDateRange({ from: undefined, to: undefined });
                          break;
                      }
                    }
                  }}
                />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter("all");
                setDateRange({ from: undefined, to: undefined });
                setFilter("");
              }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
            <Filter className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold">No quotations found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {filter || statusFilter !== "all" || dateRange.from
              ? "Try adjusting your search or filter to find what you're looking for."
              : "Get started by creating your first quotation."}
          </p>
          {!filter && statusFilter === "all" && !dateRange.from && (
            <Button
              className="mt-4"
              onClick={() => navigate("/quotation/new")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Quotation
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      {filteredData.length > 0 && (
        <>
          <div className="rounded-md border shadow-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedQuotations.length === filteredData.length &&
                        selectedQuotations.length !== 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.codeNumber && <TableHead>Quotation Number</TableHead>}
                  {visibleColumns.buyer && <TableHead>Buyer</TableHead>}
                  {visibleColumns.quotationDate && <TableHead>Quotation Date</TableHead>}
                  {visibleColumns.validUntil && <TableHead>Valid Until</TableHead>}
                  {visibleColumns.amount && <TableHead className="text-right">Amount</TableHead>}
                  {visibleColumns.status && <TableHead>Status</TableHead>}
                  {visibleColumns.emailStatus && <TableHead>Email Status</TableHead>}
                  {visibleColumns.actions && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((quotation: QuotationListItem) => (
                  <TableRow
                    key={quotation.id}
                    onDoubleClick={() => navigate(`/quotation/view`, { state: { id: quotation.id } })}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedQuotations.includes(quotation.id)}
                        onCheckedChange={(checked) =>
                          handleSelectQuotation(checked, quotation.id)
                        }
                      />
                    </TableCell>
                    {visibleColumns.codeNumber && (
                      <TableCell className="font-medium">{quotation.codeNumber}</TableCell>
                    )}
                    {visibleColumns.buyer && (
                      <TableCell>
                        {quotation.buyerName || "Not Found"}
                        <div className="text-xs text-gray-500">{quotation.buyerCode}</div>
                      </TableCell>
                    )}
                    {visibleColumns.quotationDate && (
                      <TableCell>
                        {format(new Date(quotation.quotationDate), "dd/MM/yyyy")}
                      </TableCell>
                    )}
                    {visibleColumns.validUntil && (
                      <TableCell>
                        {format(new Date(quotation.validUntil), "dd/MM/yyyy")}
                      </TableCell>
                    )}
                    {visibleColumns.amount && (
                      <TableCell className="text-right">
                        {formatTotalAmount(Number(quotation.totalPayableAmount))}
                      </TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <span className={getStatusColor(quotation.status)}>
                          {quotation.status}
                        </span>
                      </TableCell>
                    )}
                    {visibleColumns.emailStatus && (
                      <TableCell>
                        {quotation.emailStatus ? (
                          <span className={getEmailStatusColor(quotation.emailStatus)}>
                            {quotation.emailStatus}
                          </span>
                        ) : null}
                      </TableCell>
                    )}
                    {visibleColumns.actions && (
                      <TableCell>
                        <DropdownMenu
                          open={openDropdownRowId === quotation.id}
                          onOpenChange={(isOpen) =>
                            setOpenDropdownRowId(isOpen ? quotation.id : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <Button
                              variant="ghost"
                              onClick={() => navigate(`/quotation/view`, { state: { id: quotation.id } })}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </Button>
                            <DropdownMenuSeparator />
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => handleClone(quotation.id)}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Clone
                            </Button>
                            {quotation.status === QuotationStatus.DRAFT && (
                              <>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      className="w-full justify-start text-red-600"
                                      variant="ghost"
                                    >
                                      <Trash className="mr-2 h-4 w-4" />
                                      Delete
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Quotation</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete quotation{" "}
                                        {quotation.codeNumber}? This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(quotation.id)}
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-2">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {getPaginationButtons()}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Quotations;
